import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Divider } from 'primereact/divider';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import RoleDropdown from './RoleDropdown';

const AdministrationFilter = ({ 
    initialFilters = { 
        firstName: '',
        lastName: '',
        role: null
    }, 
    onApplyFilters, 
    onResetFilters,
    initiallyOpen = false, 
    className = '',
    roleList = [],
    isLoadingRoles = false
}) => {
    const { th } = useLanguage();
    const [filterOpen, setFilterOpen] = useState(initiallyOpen);
    const [isApplying, setIsApplying] = useState(false);

    const defaults = useMemo(
        () => ({
            firstName: '',
            lastName: '',
            roleId: null
        }),
        []
    );

    // ✅ LOKAL STATE - Kullanıcı ne yazarsa onu tutar
    const [localFilters, setLocalFilters] = useState(defaults);

    const toggle = () => setFilterOpen((p) => !p);
    const update = (key, value) => setLocalFilters((prev) => ({ ...prev, [key]: value }));

    const handleReset = () => {
        setLocalFilters(defaults);
        onResetFilters && onResetFilters();
    };

    const handleApply = useCallback(async () => {
        setIsApplying(true);

        // Backend rolü Authority (rol id) üzerinden filtreler; bu yüzden roleId gönderiyoruz.
        const payload = {
            firstName: (localFilters.firstName || '').trim(),
            lastName: (localFilters.lastName || '').trim(),
            role: localFilters.roleId ?? null
        };

        onApplyFilters && onApplyFilters(payload);

        setTimeout(() => {
            setIsApplying(false);
        }, 500);
    }, [localFilters, onApplyFilters]);

    const onEnterApply = (e) => e.key === 'Enter' && handleApply();

    const hasActiveFilters = 
        localFilters.firstName !== '' || 
        localFilters.lastName !== '' ||
        localFilters.roleId !== null;

    return (
        <div className={`generic-table-filter ${className}`}>
            <div className="grid">
                <div className="col-12 md:col-6 flex justify-content-start">
                    <Button 
                        icon="pi pi-filter" 
                        label={th('filter.advancedFilter')} 
                        onClick={toggle} 
                        outlined={!filterOpen}
                    />
                </div>
            </div>

            {filterOpen && (
                <div className="mt-3">
                    <Divider />
                    <div className="text-xl font-medium mb-3">{th('filter.advancedFilters')}</div>

                    <div className="flex flex-wrap gap-2" style={{ alignItems: 'end' }}>
                        <div className="flex-auto" style={{ minWidth: 240, maxWidth: 300 }}>
                            <div className="field mb-2 p-1">
                                <label className="block mb-1 text-sm">{th('userAccounts.firstName')}</label>
                                <InputText 
                                    value={localFilters.firstName || ''} 
                                    onChange={(e) => update('firstName', e.target.value)} 
                                    placeholder={th('userAccounts.enterFirstName')} 
                                    className="w-full" 
                                    onKeyDown={onEnterApply}
                                />
                            </div>
                        </div>

                        <div className="flex-auto" style={{ minWidth: 240, maxWidth: 300 }}>
                            <div className="field mb-2 p-1">
                                <label className="block mb-1 text-sm">{th('userAccounts.lastName')}</label>
                                <InputText 
                                    value={localFilters.lastName || ''} 
                                    onChange={(e) => update('lastName', e.target.value)} 
                                    placeholder={th('userAccounts.enterLastName')} 
                                    className="w-full" 
                                    onKeyDown={onEnterApply}
                                />
                            </div>
                        </div>

                        <div className="flex-auto" style={{ minWidth: 240, maxWidth: 300 }}>
                            <div className="field mb-2 p-1">
                                <label className="block mb-1 text-sm">{th('systemAdministrator.userType')}</label>
                                <RoleDropdown
                                    value={localFilters.roleId}  
                                    onChange={(value) => update('roleId', value)}  
                                    roleList={roleList}
                                    isLoading={isLoadingRoles}
                                    placeholder={th('systemAdministrator.userTypePlaceholder')}
                                    className="w-full"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-content-start mt-3">
                        <Button 
                            label={th('filter.reset')} 
                            icon="pi pi-refresh" 
                            className="p-button-outlined mr-2" 
                            onClick={handleReset} 
                            type="button" 
                        />
                        <Button 
                            label={th('filter.apply')} 
                            icon="pi pi-check" 
                            onClick={handleApply} 
                            type="button" 
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdministrationFilter;